// src/hooks/useHostPermissions.ts
import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';
import {supabase} from '@/services/supabase';
import toast from 'react-hot-toast';

export interface HostGamePermission {
    id: string;
    user_id: string;
    game_instance_id: string;
    granted_by: string | null;
    granted_at: string;
    expires_at: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface GameInstance {
    id: string;
    name: string;
    display_name: string;
    description: string | null;
    url: string;
    thumbnail: string | null;
    active: boolean;
}

// Input types for mutations
export interface GrantGameAccessInput {
    userId: string;
    gameName: string;
    expiresInDays?: number;
}

export interface RevokeGameAccessInput {
    permissionId: string;
    userId: string;
}

// Response types
export interface GrantGameAccessResponse {
    success: boolean;
    permissionId: string;
}

export interface RevokeGameAccessResponse {
    success: boolean;
    permissionId: string;
    userId: string;
}

// Get all game instances
export const useGameInstances = (): UseQueryResult<GameInstance[], Error> => {
    return useQuery({
        queryKey: ['game-instances'],
        queryFn: async (): Promise<GameInstance[]> => {
            const {data, error} = await supabase
                .from('game_instances')
                .select('*')
                .order('display_name');

            if (error) throw error;
            return data || [];
        },
    });
};

// Get host permissions for a specific user
export const useHostPermissions = (hostId: string) => {
    return useQuery({
        queryKey: ['host-permissions', hostId],
        queryFn: async (): Promise<HostGamePermission[]> => {
            if (!hostId) return [];

            const {data, error} = await supabase
                .from('host_game_permissions')
                .select(`
                    *,
                    game_instances:game_instance_id (
                        id,
                        name,
                        display_name,
                        description,
                        active
                    )
                `)
                .eq('user_id', hostId) // ✅ CORRECT - user_id references game_hosts.id
                .order('granted_at', {ascending: false});

            if (error) {
                console.error('Error fetching host permissions:', error);
                throw error;
            }

            return data || [];
        },
        enabled: !!hostId,
    });
};

// Grant game access to a host
export const useGrantGameAccess = (): UseMutationResult<
    GrantGameAccessResponse,
    Error,
    GrantGameAccessInput
> => {
    const queryClient: QueryClient = useQueryClient();

    return useMutation<GrantGameAccessResponse, Error, GrantGameAccessInput>({
        mutationFn: async ({
                               userId,
                               gameName,
                               expiresInDays,
                           }: GrantGameAccessInput): Promise<GrantGameAccessResponse> => {
            // Get game instance ID
            const {data: gameInstance, error: gameError} = await supabase
                .from('game_instances')
                .select('id')
                .eq('name', gameName)
                .single();

            if (gameError || !gameInstance) {
                throw new Error(`Game '${gameName}' not found`);
            }

            // No expiration date chosen => permanent access. Passing null leaves
            // host_game_permissions.expires_at NULL in grant_host_game_access (which
            // only sets an expiry when p_expires_hours > 0), so the grant never lapses.
            // The old default of 24h silently revoked host access after a day.
            const expiresHours: number | null = expiresInDays ? expiresInDays * 24 : null;

            const {data, error} = await supabase
                .rpc('grant_host_game_access', {
                    p_host_user_id: userId,
                    p_game_instance_id: gameInstance.id,
                    p_expires_hours: expiresHours,
                    p_notes: `Granted by admin at ${new Date().toISOString()}`
                });

            if (error) {
                throw new Error(`Failed to grant game access: ${error.message}`);
            }

            return {
                success: true,
                permissionId: data
            };
        },
        onSuccess: (_, variables: GrantGameAccessInput) => {
            toast.success('Game access granted successfully');
            queryClient.invalidateQueries({queryKey: ['host-permissions', variables.userId]});
        },
        onError: (error: Error) => {
            console.error('Error granting game access:', error);
            toast.error(error.message || 'Failed to grant game access');
        },
    });
};

// Revoke game access from a host
export const useRevokeGameAccess = (): UseMutationResult<
    RevokeGameAccessResponse,
    Error,
    RevokeGameAccessInput
> => {
    const queryClient = useQueryClient();

    return useMutation<RevokeGameAccessResponse, Error, RevokeGameAccessInput>({
        mutationFn: async ({
                               permissionId,
                               userId
                           }: RevokeGameAccessInput): Promise<RevokeGameAccessResponse> => {
            const {error} = await supabase
                .from('host_game_permissions')
                .delete()
                .eq('id', permissionId);

            if (error) {
                throw new Error(`Failed to revoke game access: ${error.message}`);
            }

            return {
                success: true,
                permissionId,
                userId
            };
        },
        onSuccess: (data: RevokeGameAccessResponse) => {
            toast.success('Game access revoked successfully');
            queryClient.invalidateQueries({queryKey: ['host-permissions', data.userId]});
        },
        onError: (error: Error) => {
            console.error('Error revoking game access:', error);
            toast.error(error.message || 'Failed to revoke game access');
        },
    });
};
